import React, { useEffect, useState } from "react";
import "./CommandCenter.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const num = (v) => Number(v || 0).toLocaleString("en-US");

const pct = (v) => `${Math.round(Number(v || 0) * 1000) / 10}%`;

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "cost", label: "Business & Cost" },
  { key: "system", label: "System Health" },
];

const SEVERITY_COLOR = { critical: "critical", warn: "warn", good: "good" };

function Bar({ label, value, max, formatValue }) {
  const width = max > 0 ? Math.max((value / max) * 100, value > 0 ? 2 : 0) : 0;
  return (
    <div className="bar-row">
      <div className="bar-label">{label}</div>
      <div className="bar-track"><div className="bar-fill" style={{ width: `${width}%` }} /></div>
      <div className="bar-value mono">{formatValue ? formatValue(value) : value}</div>
    </div>
  );
}

function SpendChart({ daily }) {
  const w = 640, h = 130, padL = 30, padB = 20, padT = 10;
  const max = Math.max(0.01, ...daily.map((d) => d.spend)) * 1.15;
  const bw = daily.length ? (w - padL - 10) / daily.length : 0;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="spend-svg">
      {[0, 1, 2].map((g) => (
        <line key={g} x1={padL} x2={w} y1={padT + (h - padT - padB) * (g / 2)} y2={padT + (h - padT - padB) * (g / 2)} stroke="var(--text-faint)" strokeOpacity="0.15" />
      ))}
      {daily.map((d, i) => {
        const barH = (d.spend / max) * (h - padT - padB);
        const x = padL + i * bw + 4;
        const dateLabel = d.date ? d.date.slice(5) : "";
        return (
          <g key={d.date}>
            <rect x={x} y={h - padB - Math.max(barH, 1.5)} width={Math.max(bw - 8, 2)} height={Math.max(barH, 1.5)} rx="2" fill="var(--accent)" fillOpacity="0.8" />
            <text x={x + (bw - 8) / 2} y={h - 4} textAnchor="middle" className="axis-label">{dateLabel}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function CommandCenter({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(`${API_BASE}/command-center/bootstrap`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Command Center request failed: ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (e) {
        setError(e.message || "Failed to load Command Center.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) {
    return <div className={embedded ? "cc-loading cc-loading-embedded" : "cc-loading"}>Loading Command Center…</div>;
  }

  if (error) {
    return <div className={embedded ? "cc-error cc-loading-embedded" : "cc-error"}>Couldn't load Command Center: {error}</div>;
  }

  if (!data) return null;

  const { overview, cost, usage, system_health: system } = data;
  const maxFeatureSpend = Math.max(0.001, ...cost.by_feature.map((f) => f.spend));

  return (
    <div className={embedded ? "cc-root cc-embedded" : "cc-root"}>
      {!embedded && (
        <div className="cc-topbar">
          <div className="cc-brand">
            <div className="cc-brand-mark">CC</div>
            <div className="cc-brand-text">
              <h1>Command Center</h1>
              <p>Generated {new Date(data.generated_at).toLocaleString("en-US")}</p>
            </div>
          </div>
          <div className="cc-topbar-right">
            <div className="cc-scope-pill"><span className="cc-live-dot" /> All companies</div>
          </div>
        </div>
      )}

      {embedded && (
        <div className="cc-embedded-head">
          <h1>Command Center</h1>
          <p>Generated {new Date(data.generated_at).toLocaleString("en-US")}</p>
        </div>
      )}

      <div className="cc-tabbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`cc-tabbtn ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="cc-page">
        {activeTab === "overview" && (
          <div className="cc-tabpanel">
            <div className="cc-section-head"><h2>At a glance</h2></div>
            <div className="cc-kpi-row">
              <div className="cc-kpi-card">
                <div className="label">AI spend, this month</div>
                <div className="kpi-value">{money(overview.ai_spend_this_month)}</div>
                <div className="kpi-sub">{money(overview.ai_spend_total)} all time</div>
              </div>
              <div className="cc-kpi-card">
                <div className="label">Companies</div>
                <div className="kpi-value">{num(overview.companies_count)}</div>
                <div className="kpi-sub">{num(overview.users_count)} users · {num(overview.teams_count)} teams</div>
              </div>
              <div className="cc-kpi-card">
                <div className="label">AI job queue</div>
                <div className="kpi-value" style={{ color: overview.queue_exhausted > 0 ? "var(--critical)" : "var(--good)" }}>
                  {overview.queue_exhausted > 0 ? "Needs attention" : "Healthy"}
                </div>
                <div className="kpi-sub">{overview.queue_pending_processing} in flight · {overview.queue_exhausted} exhausted</div>
              </div>
              <div className="cc-kpi-card">
                <div className="label">Users by role</div>
                <div className="kpi-value" style={{ fontSize: 20 }}>{usage.by_role.length} role{usage.by_role.length === 1 ? "" : "s"}</div>
                <div className="kpi-sub">{usage.by_role.map((r) => `${r.role} (${r.count})`).join(" · ") || "—"}</div>
              </div>
            </div>

            <div className="cc-section-head"><h2>Needs your attention</h2></div>
            <div className="cc-panel">
              <table>
                <thead><tr><th>Signal</th><th>Detail</th><th className="num-cell">Severity</th></tr></thead>
                <tbody>
                  {overview.attention.map((a, i) => (
                    <tr key={i}>
                      <td>{a.title}</td>
                      <td className="dim">{a.detail}</td>
                      <td className="num-cell">
                        <span className={`pill ${SEVERITY_COLOR[a.severity] || "neutral"}`}>
                          <span className="dot" />{a.severity === "critical" ? "High" : a.severity === "warn" ? "Medium" : "OK"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "cost" && (
          <div className="cc-tabpanel">
            <div className="cc-section-head"><h2>Spend</h2></div>
            <div className="cc-kpi-row">
              <div className="cc-kpi-card">
                <div className="label">Total AI spend (all time)</div>
                <div className="kpi-value">{money(cost.total_spend)}</div>
                <div className="kpi-sub">{num(cost.total_calls)} calls · {num(cost.total_tokens)} tokens</div>
              </div>
              <div className="cc-kpi-card">
                <div className="label">Projected monthly run-rate</div>
                <div className="kpi-value">≈{money(cost.projected_monthly_runrate)}</div>
                <div className="kpi-sub">based on the last 30 days</div>
              </div>
              <div className="cc-kpi-card">
                <div className="label">Failed calls (wasted spend)</div>
                <div className="kpi-value">{money(cost.failed_calls_cost)}</div>
                <div className="kpi-sub">{cost.failed_calls_count} failed calls</div>
              </div>
            </div>

            <div className="cc-grid-2">
              <div className="cc-panel">
                <div className="panel-title">Daily spend, last 14 days</div>
                <SpendChart daily={cost.daily} />
              </div>
              <div className="cc-panel">
                <div className="panel-title">Cost by feature</div>
                {cost.by_feature.map((f) => (
                  <Bar key={f.feature} label={f.feature} value={f.spend} max={maxFeatureSpend} formatValue={money} />
                ))}
              </div>
            </div>

            <div className="cc-section-head"><h2>Spend by company</h2></div>
            <div className="cc-panel">
              <table>
                <thead><tr><th>Company</th><th className="num-cell">Users</th><th className="num-cell">Calls</th><th className="num-cell">AI spend</th></tr></thead>
                <tbody>
                  {cost.by_company.map((c) => (
                    <tr key={c.tenant}>
                      <td>{c.tenant}</td>
                      <td className="num-cell mono">{num(c.users)}</td>
                      <td className="num-cell mono">{num(c.calls)}</td>
                      <td className="num-cell mono">{money(c.spend)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === "system" && (
          <div className="cc-tabpanel">
            <div className="cc-section-head"><h2>AI job queue</h2></div>
            <div className="cc-kpi-row">
              <div className="cc-kpi-card">
                <div className="label">Pending / processing</div>
                <div className="kpi-value">{num(system.queue.pending + system.queue.processing)}</div>
              </div>
              <div className="cc-kpi-card">
                <div className="label">Oldest pending job</div>
                <div className="kpi-value">
                  {system.queue.oldest_pending_age_seconds == null
                    ? "—"
                    : `${Math.round(system.queue.oldest_pending_age_seconds / 60)}m`}
                </div>
              </div>
              <div className="cc-kpi-card">
                <div className="label">Retry rate (7d)</div>
                <div className="kpi-value">{pct(system.queue.retry_rate_7d)}</div>
              </div>
              <div className="cc-kpi-card">
                <div className="label">Exhausted (needs a human)</div>
                <div className="kpi-value" style={{ color: system.queue.exhausted > 0 ? "var(--critical)" : "var(--good)" }}>
                  {num(system.queue.exhausted)}
                </div>
              </div>
            </div>

            <div className="cc-section-head">
              <h2>Infrastructure {!system.infra.available && <span className="placeholder-badge">Unavailable</span>}</h2>
            </div>
            <div className="cc-grid-3">
              <div className="cc-panel">
                <div className="panel-title">CPU utilization</div>
                {system.infra.available ? (
                  <>
                    <div className="kpi-value" style={{ fontSize: 22 }}>{system.infra.cpu_percent}%</div>
                    <div className="healthbar"><div style={{ width: `${system.infra.cpu_percent}%`, background: system.infra.cpu_percent > 80 ? "var(--critical)" : "var(--good)" }} /></div>
                  </>
                ) : <div className="dim">psutil unavailable on this host</div>}
              </div>
              <div className="cc-panel">
                <div className="panel-title">Memory (RAM)</div>
                {system.infra.available ? (
                  <>
                    <div className="kpi-value" style={{ fontSize: 22 }}>{system.infra.memory_percent}%</div>
                    <div className="healthbar"><div style={{ width: `${system.infra.memory_percent}%`, background: system.infra.memory_percent > 80 ? "var(--critical)" : "var(--warn)" }} /></div>
                    <div className="kpi-sub">{system.infra.memory_used_gb} / {system.infra.memory_total_gb} GB</div>
                  </>
                ) : <div className="dim">psutil unavailable on this host</div>}
              </div>
              <div className="cc-panel">
                <div className="panel-title">DB pool</div>
                {system.infra.db_pool_size != null ? (
                  <>
                    <div className="kpi-value" style={{ fontSize: 22 }}>{system.infra.db_pool_checked_out} / {system.infra.db_pool_size}</div>
                    <div className="healthbar"><div style={{ width: `${(system.infra.db_pool_checked_out / Math.max(system.infra.db_pool_size, 1)) * 100}%`, background: "var(--good)" }} /></div>
                  </>
                ) : <div className="dim">Pool stats unavailable</div>}
              </div>
            </div>

            <div className="cc-section-head"><h2>Is Celery keeping up?</h2></div>
            <div className="cc-panel">
              <table>
                <thead><tr><th>Scheduled job</th><th>Cadence</th><th>Last run</th><th>Status</th><th>Active</th></tr></thead>
                <tbody>
                  {system.scheduled_jobs.map((j) => (
                    <tr key={j.name}>
                      <td>{j.name}</td>
                      <td className="dim">{j.frequency}</td>
                      <td className="dim">{j.last_execution_date ? new Date(j.last_execution_date).toLocaleString("en-US") : "never"}</td>
                      <td>
                        {j.last_status === "executed" ? (
                          <span className="pill good"><span className="dot" />Executed</span>
                        ) : j.last_status === "pending" ? (
                          <span className="pill info"><span className="dot" />Scheduled</span>
                        ) : (
                          <span className="pill neutral"><span className="dot" />{j.last_status || "—"}</span>
                        )}
                      </td>
                      <td>{j.active ? <span className="pill good"><span className="dot" />On</span> : <span className="pill neutral"><span className="dot" />Off</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
